import type { JsonValue } from '@backstage/types';

import { ConfigMergeError } from './config-merge-error';
import { deepMerge, isJsonObject } from './json';
import type { RegistryEntry } from './types';

/**
 * Identity for an `app.extensions` item.
 *
 * - A bare string is its own identity (`"page:x"` -> `"page:x"`).
 * - A single-key object uses that key (`{ "page:x": {...} }` -> `"page:x"`).
 *
 * Every other shape is malformed and raises a {@link ConfigMergeError} BEFORE any
 * `Object.keys` call that would throw natively (a bare `null`, a number, a boolean,
 * an array, an empty object, an object with more than one key, or a single-key object
 * whose key is the empty string, which would yield an empty, colliding identity).
 */
export function extensionIdentity(item: JsonValue, context: string): string {
  if (typeof item === 'string') {
    return item;
  }

  if (!isJsonObject(item)) {
    throw new ConfigMergeError(
      'app.extensions item must be a string or a single-key object',
      'app.extensions',
      context,
    );
  }

  const keys = Object.keys(item);
  if (keys.length !== 1) {
    throw new ConfigMergeError(
      `app.extensions object item must have exactly one key, found ${keys.length}`,
      'app.extensions',
      context,
    );
  }

  const key = keys[0];
  if (key === undefined) {
    throw new ConfigMergeError(
      'app.extensions object item has no key',
      'app.extensions',
      context,
    );
  }
  if (key === '') {
    throw new ConfigMergeError(
      'app.extensions object item has an empty key (empty identity)',
      'app.extensions',
      context,
    );
  }

  return key;
}

/**
 * Identity for a `catalog.locations` item: `` `${type}:${target}` ``.
 *
 * Injectivity relies on `type` being a colon-free token (Backstage location types
 * are `url`, `file`, ...); a colon in `target` is harmless because identities are
 * only ever compared within the `catalog.locations` item set (group-by-path). A
 * missing `type` or `target` is malformed and raises a {@link ConfigMergeError}.
 */
export function locationIdentity(item: JsonValue, context: string): string {
  if (!isJsonObject(item)) {
    throw new ConfigMergeError(
      'catalog.locations item must be an object',
      'catalog.locations',
      context,
    );
  }

  const { target, type } = item;
  if (typeof type !== 'string') {
    throw new ConfigMergeError(
      'catalog.locations item is missing a string "type"',
      'catalog.locations',
      context,
    );
  }
  if (typeof target !== 'string') {
    throw new ConfigMergeError(
      'catalog.locations item is missing a string "target"',
      'catalog.locations',
      context,
    );
  }

  return `${type}:${target}`;
}

/**
 * Merge two same-identity `app.extensions` items. Both single-key objects deep-merge
 * their inner value under the shared key; any other combination (string vs object,
 * two strings, object vs string) lets the later item win wholesale. That "later wins"
 * is how an item-level `null` propagates: `{ id: null }` overwrites `{ id: {...} }`.
 */
export function mergeExtensionItems(
  earlier: JsonValue,
  later: JsonValue,
): JsonValue {
  if (isJsonObject(earlier) && isJsonObject(later)) {
    const earlierKey = Object.keys(earlier)[0];
    const laterKey = Object.keys(later)[0];
    const earlierInner =
      earlierKey === undefined ? undefined : earlier[earlierKey];
    const laterInner = laterKey === undefined ? undefined : later[laterKey];

    if (
      laterKey !== undefined &&
      isJsonObject(earlierInner) &&
      isJsonObject(laterInner)
    ) {
      return { [laterKey]: deepMerge(earlierInner, laterInner) };
    }
  }

  return later;
}

/**
 * Merge two same-identity `catalog.locations` items. These are flat multi-field
 * objects, so the whole objects deep-merge directly (later fields win, fields only on
 * the earlier item survive). Identity validation guarantees both are objects, but the
 * guards keep the merge total for the type system.
 */
export function mergeLocationItems(
  earlier: JsonValue,
  later: JsonValue,
): JsonValue {
  if (isJsonObject(earlier) && isJsonObject(later)) {
    return deepMerge(earlier, later);
  }

  return later;
}

/**
 * The identity registry: the single source of truth for which array paths get
 * per-item identity merge, and how items at that path are merged. Adding a new
 * registered path is a one-line addition here.
 */
export const REGISTRY: readonly RegistryEntry[] = [
  {
    identity: extensionIdentity,
    merge: mergeExtensionItems,
    path: 'app.extensions',
  },
  {
    identity: locationIdentity,
    merge: mergeLocationItems,
    path: 'catalog.locations',
  },
];
